import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { StructuralPieceId, UnitTypeId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import type { ItemFactor } from './own-factor';
import { pieceNameTooLong, pieceNameTooLongMessage } from './pile';
import type { Point } from './polygon';
import type { SteelRate } from './steel';

/**
 * O muro de contenção e o pilar de contenção (Fase 27, etapa 4 — D9, F2, F6, F7, RN-372).
 *
 * Os dois são peças da família da estaca (ADR-175), na área comum do desenho da Contenção
 * (ou da Fundação) — RN-361 —, e nenhum soma em PAREDE nem em PILAR (F2): a alvenaria não
 * mede o muro, e a concretagem dos pilares da estrutura não mede o pilar da contenção.
 *
 * - O **muro** (e a cortina) é desenhado como a parede: dois pontos, altura e espessura. Mede a
 *   face em elevação (comprimento × altura, o m² de MURO DE CONTENÇÃO e de CORTINA ATIRANTADA
 *   — F7), o volume, o comprimento e o aço pela taxa.
 * - O **pilar** é moldado com fôrma (F6): centro, seção e altura, com o ângulo do muro em que
 *   está encaixado. Mede volume, faces (a fôrma), altura, contagem e aço.
 *
 * > **A regra em uma linha (RN-372):** o pilar encaixado no muro tira do muro o que ocupa — a
 * > face na largura dele e o volume na espessura do muro —, e o muro tira do pilar as duas
 * > faces que ficam dentro dele, onde não há fôrma.
 */

export const RETAINING_ERROR = {
  NAME_REQUIRED: 'TAKEOFF.RETAINING_NAME_REQUIRED',
  NAME_TAKEN: 'TAKEOFF.RETAINING_NAME_TAKEN',
  NAME_TOO_LONG: 'TAKEOFF.RETAINING_NAME_TOO_LONG',
  INVALID_DIMENSION: 'TAKEOFF.RETAINING_INVALID_DIMENSION',
  ZERO_LENGTH: 'TAKEOFF.RETAINING_ZERO_LENGTH',
} as const;

export interface RetainingWall {
  readonly id: StructuralPieceId;
  readonly unitTypeId: UnitTypeId;
  readonly kind: 'MURO_CONTENCAO';
  /** "MC1". Único entre os muros do desenho. */
  readonly name: string;
  readonly from: Point;
  readonly to: Point;
  /** Metros. */
  readonly height: number;
  /** Metros. */
  readonly thickness: number;
  /**
   * O fator próprio de cada item neste muro, por cima do índice da obra (Fase 42, etapa 3 — G3;
   * RN-571). Sem tipo. Ausente: o rascunho de antes; vazio: nenhum.
   */
  readonly factors?: readonly ItemFactor[];
  /**
   * A taxa de aço própria deste muro, kg/m³ por bitola (Fase 42, etapa 3 — RN-572). Vale
   * inteira, por cima da da disciplina — a lista do muro, quando a disciplina mistura os dois
   * elementos (RN-573). Ausente: o rascunho de antes; vazia: nenhuma.
   */
  readonly steelRates?: readonly SteelRate[];
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface RetainingColumn {
  readonly id: StructuralPieceId;
  readonly unitTypeId: UnitTypeId;
  readonly kind: 'PILAR_CONTENCAO';
  /** "PC1". Único entre os pilares de contenção do desenho. */
  readonly name: string;
  readonly center: Point;
  /** Metros — ao longo do muro, quando encaixado. O primeiro número de "30 × 50". */
  readonly sectionWidth: number;
  /** Metros — na direção da espessura do muro. */
  readonly sectionDepth: number;
  /** Metros. */
  readonly height: number;
  /**
   * O ângulo da seção, em graus, medido como o eixo do muro (0 = largura em x). A planta o
   * copia do muro em que o pilar foi encaixado; solto, é 0.
   */
  readonly angle: number;
  /**
   * O fator próprio de cada item neste pilar de contenção, por cima do índice da obra (Fase 42,
   * etapa 3 — G3; RN-571). Sem tipo. Ausente: o rascunho de antes; vazio: nenhum.
   */
  readonly factors?: readonly ItemFactor[];
  /**
   * A taxa de aço própria deste pilar de contenção, kg/m³ por bitola (Fase 42, etapa 3 —
   * RN-572). Vale inteira, por cima da da disciplina — a lista do pilar, quando a disciplina
   * mistura os dois elementos (RN-573). Ausente: o rascunho de antes; vazia: nenhuma.
   */
  readonly steelRates?: readonly SteelRate[];
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

// ── As conferências ─────────────────────────────────────────────────────────────

function positivo(valor: number): boolean {
  return Number.isFinite(valor) && valor > 0;
}

function tres(valor: number): number {
  return Math.round(valor * 1000) / 1000;
}

function distancia(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function nome(
  rotulo: string,
  name: string,
  siblings: readonly {
    readonly id: StructuralPieceId;
    readonly name: string;
    readonly deletedAt?: string;
  }[],
  selfId: StructuralPieceId | undefined,
): Result<string, DomainError> {
  const texto = name.trim();
  if (texto === '') {
    return err(domainError(RETAINING_ERROR.NAME_REQUIRED, `O nome do ${rotulo} é obrigatório.`));
  }
  if (pieceNameTooLong(texto)) {
    return err(domainError(RETAINING_ERROR.NAME_TOO_LONG, pieceNameTooLongMessage(rotulo)));
  }
  const chave = normalizeName(texto);
  if (
    siblings.some(
      (outro) =>
        outro.deletedAt === undefined && outro.id !== selfId && normalizeName(outro.name) === chave,
    )
  ) {
    return err(
      domainError(RETAINING_ERROR.NAME_TAKEN, `Já existe o ${rotulo} ${texto} neste desenho.`, {
        name: texto,
      }),
    );
  }
  return ok(texto);
}

export function checkRetainingWallFields(
  fields: {
    readonly name: string;
    readonly from: Point;
    readonly to: Point;
    readonly height: number;
    readonly thickness: number;
  },
  siblings: readonly Pick<RetainingWall, 'id' | 'name' | 'deletedAt'>[],
  selfId?: StructuralPieceId,
): Result<
  {
    readonly name: string;
    readonly from: Point;
    readonly to: Point;
    readonly height: number;
    readonly thickness: number;
  },
  DomainError
> {
  const texto = nome('muro', fields.name, siblings, selfId);
  if (!texto.ok) return texto;
  if (distancia(fields.from, fields.to) < 0.01) {
    return err(
      domainError(
        RETAINING_ERROR.ZERO_LENGTH,
        `O muro ${texto.value} precisa de dois pontos diferentes.`,
      ),
    );
  }
  if (!positivo(fields.height) || !positivo(fields.thickness)) {
    return err(
      domainError(
        RETAINING_ERROR.INVALID_DIMENSION,
        `A altura e a espessura do muro ${texto.value} devem ser maiores que zero.`,
        { height: fields.height, thickness: fields.thickness },
      ),
    );
  }
  return ok({
    name: texto.value,
    from: fields.from,
    to: fields.to,
    height: tres(fields.height),
    thickness: tres(fields.thickness),
  });
}

export function checkRetainingColumnFields(
  fields: {
    readonly name: string;
    readonly center: Point;
    readonly sectionWidth: number;
    readonly sectionDepth: number;
    readonly height: number;
    readonly angle?: number;
  },
  siblings: readonly Pick<RetainingColumn, 'id' | 'name' | 'deletedAt'>[],
  selfId?: StructuralPieceId,
): Result<
  {
    readonly name: string;
    readonly center: Point;
    readonly sectionWidth: number;
    readonly sectionDepth: number;
    readonly height: number;
    readonly angle: number;
  },
  DomainError
> {
  const texto = nome('pilar de contenção', fields.name, siblings, selfId);
  if (!texto.ok) return texto;
  if (
    !positivo(fields.sectionWidth) ||
    !positivo(fields.sectionDepth) ||
    !positivo(fields.height)
  ) {
    return err(
      domainError(
        RETAINING_ERROR.INVALID_DIMENSION,
        `A seção e a altura do pilar ${texto.value} devem ser maiores que zero.`,
      ),
    );
  }
  const angulo = Number.isFinite(fields.angle ?? 0) ? (((fields.angle ?? 0) % 360) + 360) % 360 : 0;
  return ok({
    name: texto.value,
    center: fields.center,
    sectionWidth: tres(fields.sectionWidth),
    sectionDepth: tres(fields.sectionDepth),
    height: tres(fields.height),
    angle: Math.round(angulo * 1000) / 1000,
  });
}

// ── O encaixe (RN-372) ──────────────────────────────────────────────────────────

/** O ângulo do eixo do muro, em graus — o que o pilar encaixado copia. */
export function wallAngle(wall: Pick<RetainingWall, 'from' | 'to'>): number {
  const graus = (Math.atan2(wall.to.y - wall.from.y, wall.to.x - wall.from.x) * 180) / Math.PI;
  return Math.round((((graus % 360) + 360) % 360) * 1000) / 1000;
}

/**
 * O pilar está encaixado neste muro? O centro dele cai dentro da faixa do muro: a até meia
 * espessura do eixo, e entre as duas pontas. É o que a planta faz ao clicar sobre o muro.
 */
export function isColumnInWall(
  column: Pick<RetainingColumn, 'center'>,
  wall: Pick<RetainingWall, 'from' | 'to' | 'thickness'>,
): boolean {
  const dx = wall.to.x - wall.from.x;
  const dy = wall.to.y - wall.from.y;
  const comprimento2 = dx * dx + dy * dy;
  if (comprimento2 === 0) return false;
  const t =
    ((column.center.x - wall.from.x) * dx + (column.center.y - wall.from.y) * dy) / comprimento2;
  if (t < 0 || t > 1) return false;
  const px = wall.from.x + t * dx;
  const py = wall.from.y + t * dy;
  return Math.hypot(column.center.x - px, column.center.y - py) <= wall.thickness / 2 + 1e-9;
}

/** O muro em que o pilar está encaixado — o primeiro vivo que o contém. */
export function hostWall<W extends Pick<RetainingWall, 'from' | 'to' | 'thickness' | 'deletedAt'>>(
  column: Pick<RetainingColumn, 'center'>,
  walls: readonly W[],
): W | undefined {
  return walls.find((wall) => wall.deletedAt === undefined && isColumnInWall(column, wall));
}

// ── As contas ───────────────────────────────────────────────────────────────────

type MuroDaConta = Pick<RetainingWall, 'from' | 'to' | 'height' | 'thickness' | 'deletedAt'>;
type PilarDaConta = Pick<
  RetainingColumn,
  'center' | 'sectionWidth' | 'sectionDepth' | 'height' | 'deletedAt'
> & {
  /**
   * O ângulo da seção (graus). Sem ele, o pilar é tomado como alinhado ao muro que o encaixa —
   * o que a planta faz ao encaixá-lo.
   */
  readonly angle?: number;
};

function vivos<T extends { readonly deletedAt?: string }>(lista: readonly T[]): T[] {
  return lista.filter((item) => item.deletedAt === undefined);
}

/**
 * Onde o pilar cai sobre o eixo do muro. `extensao` é o quanto a seção ocupa ao longo do eixo
 * (a largura se está alinhada ao muro, a profundidade se está de través — pelo ângulo relativo);
 * `transversal`, o quanto ocupa de través. `de`/`ate` são as posições (m) da seção no eixo,
 * contadas da ponta `from`, e `sobreposto` é a parte delas que cai dentro do trecho [0, L].
 */
function posicaoNoMuro(
  pilar: PilarDaConta,
  muro: Pick<RetainingWall, 'from' | 'to'>,
): {
  readonly extensao: number;
  readonly transversal: number;
  readonly sobreposto: number;
  readonly facesDentro: number;
} {
  const comprimento = distancia(muro.from, muro.to);
  const ux = comprimento === 0 ? 1 : (muro.to.x - muro.from.x) / comprimento;
  const uy = comprimento === 0 ? 0 : (muro.to.y - muro.from.y) / comprimento;
  const radianos =
    pilar.angle === undefined || !Number.isFinite(pilar.angle)
      ? 0
      : ((pilar.angle - wallAngle(muro)) * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radianos));
  const sen = Math.abs(Math.sin(radianos));
  const extensao = pilar.sectionWidth * cos + pilar.sectionDepth * sen;
  const transversal = pilar.sectionWidth * sen + pilar.sectionDepth * cos;
  const centro = (pilar.center.x - muro.from.x) * ux + (pilar.center.y - muro.from.y) * uy;
  const de = centro - extensao / 2;
  const ate = centro + extensao / 2;
  const sobreposto = Math.max(0, Math.min(comprimento, ate) - Math.max(0, de));
  const folga = 1e-9;
  const facesDentro =
    (de >= -folga && de <= comprimento + folga ? 1 : 0) +
    (ate >= -folga && ate <= comprimento + folga ? 1 : 0);
  return { extensao, transversal, sobreposto, facesDentro };
}

/** Comprimento do eixo do muro. m, duas casas. */
export function retainingWallLength(wall: Pick<RetainingWall, 'from' | 'to'>): number {
  return roundQuantity(distancia(wall.from, wall.to), 2);
}

/** O que os pilares encaixados tiram da face do muro: a parte da seção que cai sobre ele. */
function faceDosPilares(wall: MuroDaConta, columns: readonly PilarDaConta[]): number {
  return vivos(columns)
    .filter((pilar) => isColumnInWall(pilar, wall))
    .reduce(
      (soma, pilar) =>
        soma + posicaoNoMuro(pilar, wall).sobreposto * Math.min(pilar.height, wall.height),
      0,
    );
}

/**
 * A face do muro em elevação — comprimento × altura, sem o que os pilares encaixados ocupam
 * (o trecho da seção que cai sobre o muro, ao longo do eixo dele × a altura que os dois têm em
 * comum). Pilar na ponta só tira o que está sobre o muro; pilar de través tira a profundidade.
 * m², duas casas.
 */
export function retainingWallFace(
  wall: MuroDaConta,
  columns: readonly PilarDaConta[] = [],
): number {
  const bruta = distancia(wall.from, wall.to) * wall.height;
  return roundQuantity(Math.max(0, bruta - faceDosPilares(wall, columns)), 2);
}

/** O concreto do muro: a face (já sem os pilares) × espessura. m³, três casas. */
export function retainingWallVolume(
  wall: MuroDaConta,
  columns: readonly PilarDaConta[] = [],
): number {
  const bruta = distancia(wall.from, wall.to) * wall.height;
  return roundQuantity(Math.max(0, bruta - faceDosPilares(wall, columns)) * wall.thickness, 3);
}

/** O concreto do pilar: seção × altura — inteiro, porque o muro já cedeu a parte dele. m³. */
export function retainingColumnVolume(column: PilarDaConta): number {
  return roundQuantity(column.sectionWidth * column.sectionDepth * column.height, 3);
}

/**
 * As faces do pilar — a fôrma. Solto, as quatro: perímetro × altura. Encaixado, a face que
 * encosta em cada muro que ele toca (uma de cada lado do pilar, as duas que ficam dentro do
 * trecho do muro) não leva fôrma: o concreto do muro encosta ali. Num cruzamento são as de
 * todos os muros, não só do primeiro. m², duas casas.
 */
export function retainingColumnFaces(
  column: PilarDaConta,
  walls: readonly MuroDaConta[] = [],
): number {
  const todas = 2 * (column.sectionWidth + column.sectionDepth) * column.height;
  // Cada face encostada tem a espessura do muro (ou menos, se o pilar é mais estreito) × a
  // altura comum — o resto da profundidade do pilar, se sobra, leva fôrma.
  const encostada = vivos(walls)
    .filter((muro) => isColumnInWall(column, muro))
    .reduce((soma, muro) => {
      const { transversal, facesDentro } = posicaoNoMuro(column, muro);
      return (
        soma +
        facesDentro * Math.min(transversal, muro.thickness) * Math.min(column.height, muro.height)
      );
    }, 0);
  return roundQuantity(Math.max(0, todas - encostada), 2);
}

// ── A sobreposição ──────────────────────────────────────────────────────────────

type PilarNoPlano = Pick<
  RetainingColumn,
  'center' | 'sectionWidth' | 'sectionDepth' | 'angle' | 'deletedAt'
>;

function cantos(pilar: PilarNoPlano): readonly Point[] {
  const radianos = (pilar.angle * Math.PI) / 180;
  const ux = Math.cos(radianos);
  const uy = Math.sin(radianos);
  const largura = pilar.sectionWidth / 2;
  const profundidade = pilar.sectionDepth / 2;
  const canto = (a: number, b: number): Point => ({
    x: pilar.center.x + a * largura * ux - b * profundidade * uy,
    y: pilar.center.y + a * largura * uy + b * profundidade * ux,
  });
  return [canto(1, 1), canto(1, -1), canto(-1, -1), canto(-1, 1)];
}

/**
 * As seções dos dois pilares se sobrepõem? (Retângulos girados — separação por eixos.) Encostar
 * pelo lado não é sobrepor: só conta se há área em comum. Dois pilares assim descontariam o
 * mesmo trecho do muro duas vezes (RN-372).
 */
export function retainingColumnsOverlap(a: PilarNoPlano, b: PilarNoPlano): boolean {
  const folga = 1e-6;
  const cantosA = cantos(a);
  const cantosB = cantos(b);
  const eixos = [a, b].flatMap((pilar) => {
    const radianos = (pilar.angle * Math.PI) / 180;
    return [
      { x: Math.cos(radianos), y: Math.sin(radianos) },
      { x: -Math.sin(radianos), y: Math.cos(radianos) },
    ];
  });
  return eixos.every((eixo) => {
    const projetar = (pontos: readonly Point[]) => {
      const valores = pontos.map((ponto) => ponto.x * eixo.x + ponto.y * eixo.y);
      return { min: Math.min(...valores), max: Math.max(...valores) };
    };
    const pa = projetar(cantosA);
    const pb = projetar(cantosB);
    return pa.max - pb.min > folga && pb.max - pa.min > folga;
  });
}
